import {Article} from '../article';
import {EnumCorridorColor} from '../../../enum/enum-corridor-color';
import {PricingItem} from '../pricing-item';
import {generateUniqueId} from '../../../../utils/math-utils';
import {EnumPricingItemType} from '../pricing-item-type';
import {DevicePricingItem} from '../device-pricing-item';
import {BillingLine} from '../../../billing-line/billing-line.model';
import {ServiceConfig} from '../../../referential/service-config.model';
import {PricingItemMapper} from './pricing-item.mapper';
import {ConsumablePricingItem} from '../consumable-pricing-item';
import { ProductPricingV2 } from '../../../productv2/productpricingv2.model';
import { ProductV2 } from '../../../productv2/productv2.model';
import { PriceList } from '../../../productv2/pricelist.model';
import { DiscoPricingItemInitializer } from 'src/app/components/article-selection-form/disco/disco.pricing-item-initializer';


export class DiscoPricingItemMapper extends PricingItemMapper {

    mapPricingItemsFromCustomerContracts(mapCustomerContractsByServiceCode: Map<string, BillingLine[]>, service: ServiceConfig, products: ProductV2[], cmcProducts: ProductV2[], referential: any, isServiceCmc: boolean): PricingItem[] {
        let pricingItems: PricingItem[] = [];
        const customerContratcs = mapCustomerContractsByServiceCode.get(service.serviceCode) ?? [];
        customerContratcs.forEach((cc, idx) => {
            let standardProduct = products?.find(p => p.code === cc.articleCode);
            let productFromDb: ProductV2;
            if (cc.keyAccountCode && isServiceCmc) {
                productFromDb = this.getCmcProductWithBillingLine(cmcProducts, cc, standardProduct);
            } else {
                productFromDb = standardProduct;
            }
            if (productFromDb) {
                pricingItems.push(this.mapPricingItemFromProduct(productFromDb, cc, referential, idx.toString()));
            } else {
                pricingItems.push(this.mapPricingItemFromCustomerContract(cc, referential, isServiceCmc, standardProduct, idx.toString()));
            }
        });
        return pricingItems;
    }

    private getCmcProductWithBillingLine(cmcProducts: ProductV2[], cc: BillingLine, product: ProductV2): ProductV2 {
        let cmcProduct = cmcProducts.find(p => p.code === cc.articleCode);

        if (cmcProduct && product) {
            cmcProduct["status"] = product["status"];
            let productPricing = this.findMatchingProductPricing(cmcProduct.priceList?.productPricings, cc);
            if (!productPricing) {
                this.completeProductPricingsWithProduct(cmcProduct, product);
            }
            return cmcProduct;
        }
        return cmcProduct;
    }

    protected completeProductPricingsWithProduct(cmcProduct: ProductV2, product: ProductV2) {
        let frequencyList = Array.from(
            new Set(
                cmcProduct.priceList.productPricings.flatMap(
                    pp => pp.productPricingDetails['frequency']
                )
            )
        );
        let standardProductPricings = product.priceList?.productPricings.filter(pp => !frequencyList.includes(pp.productPricingDetails['frequency']));
        if(standardProductPricings){
            standardProductPricings.forEach(pp => {
                pp['type'] = 'STANDARD';
            });
            cmcProduct.priceList.productPricings = cmcProduct.priceList.productPricings.concat(standardProductPricings);
            cmcProduct['isCmcWithStandardProductPricing'] = true;
        }
    }

    protected mapPricingItemFromProduct(product: ProductV2, cc: BillingLine, referential: any, idx: string) {
        let pricingItem = this.mapProductToPricingItemWithCustomerContract(product, cc, idx);
        const frDiscoPricingItemInitializer = new DiscoPricingItemInitializer(new DiscoPricingItemMapper(true));

        const prefilledInputs = frDiscoPricingItemInitializer.getPrefilledInputs(product.priceList?.productPricings, referential, cc);
        const userInputs = frDiscoPricingItemInitializer.initUserInputs(cc, prefilledInputs, pricingItem);

        return pricingItem.copyWith({ userInputs, prefilledInputs });
    }

    protected findMatchingProductPricing(productPricings: ProductPricingV2[], customerContract: BillingLine) {
        return productPricings?.find(pp => pp.frequency === customerContract.serviceFrequency
            && Number(pp.minQuantity) <= customerContract.monthlyQuantity
            && Number(pp.maxQuantity) >= customerContract.monthlyQuantity
        );
    }

    mapProductToPricingItemWithCustomerContract(product: ProductV2, customerContract: BillingLine, idx?: string): PricingItem {
        const generatedId = generateUniqueId();
        if (customerContract.isConsumable) {
            return new ConsumablePricingItem().copyWith({
                id: generateUniqueId(),
                matchingId: generatedId,
                family: product.productDetails?.family,
                isValid: false,
                consumable: this.mapProductToRenewalArticle(product, product.productDetails, product.priceList, customerContract),
                pricingItemType: EnumPricingItemType.CONSUMABLE,
                isCmc: product.isCmc,
                displayOrder: idx,
                consoType: customerContract.consoType,
                consumableExtraPrice: String(customerContract?.unitPriceExtra || '0.00')
            });
        }

        return new DevicePricingItem().copyWith({
            id: generateUniqueId(),
            matchingId: generatedId,
            family: product.productDetails?.family,
            isValid: false,
            device: this.mapProductToRenewalArticle(product, product.productDetails, product.priceList, customerContract),
            pricingItemType: EnumPricingItemType.DEVICE,
            isCmc: product.isCmc,
            displayOrder: idx
        });
    }

    private mapProductToRenewalArticle(product: ProductV2, productDetails: Record<string, any>, classification: PriceList, cc: BillingLine): Article {
        return new Article().copyWith({
            label: cc?.articleName,
            codeElis: product?.code,
            articleDetails: productDetails,
            productPricings: this.mapProductPricing(classification?.productPricings),
            appliedPrice: String(cc?.unitPrice || '0.00'),
            appliedPriceColor: EnumCorridorColor.GRAY,
            status: product?.['status'],
            groupingCode: classification?.groupingCode,
            isCmcWithStandardProductPricing: product?.['isCmcWithStandardProductPricing'],
            isRenewal: true,
            oldAppliedPrice: String(cc?.unitPrice || '0.00')
        });
    }


    private mapPricingItemFromCustomerContract(cc: BillingLine, referential: any, isServiceCmc: boolean, product?: ProductV2, idx?: string) {
       // let generatedProduct = new AtomicProductV2();
        cc = Object.assign(new BillingLine(), cc);
        let isConsumable = cc.isConsumable;
        let productPricings = cc.keyAccountCode && isServiceCmc ? product?.priceList?.productPricings : [];
        const article = new Article().copyWith({
            codeElis: cc.articleCode,
            label: cc.articleName,
            appliedPrice: String(cc.unitPrice),
            appliedPriceColor: EnumCorridorColor.GRAY,
            productPricings: this.mapProductPricing(productPricings),
            existsInProductDb: false,
            status: product?.['status'],
            isRenewal: true,
            isCmcWithStandardProductPricing: Boolean(isServiceCmc && cc.keyAccountCode) ? true : false,
            oldAppliedPrice: String(cc?.unitPrice || '0.00')
        });
        const foundBillingMode = referential.billingModes.find(item => item.key == cc.chargingType);
        let foundFrequency = referential.frequencies.find(item => item.frequency == cc.serviceFrequency);
        if (foundFrequency) {
            foundFrequency = ({
                key: foundFrequency.frequency,
                value: foundFrequency.frequency
            });
        }
        let prefilledInputs = this.getPrefilledInputs(cc, referential, productPricings);
        const generatedId = generateUniqueId();
        if (isConsumable) {
            const userInputs = {
                billingMode: foundBillingMode || cc.chargingType,
                frequency: foundFrequency || cc.serviceFrequency,
                consumableQuantity: cc.monthlyQuantity,
                franchiseConso: cc.getFranchiseConso(),
            };

            return new ConsumablePricingItem().copyWith({
                id: generatedId,
                consumable: article,
                consumableExtraPrice: String(cc?.unitPriceExtra || '0.00'),
                userInputs,
                prefilledInputs,
                pricingItemType: EnumPricingItemType.CONSUMABLE,
                isCmc: Boolean(isServiceCmc && cc.keyAccountCode),
                consoType: cc.consoType,
                matchingId: generatedId,
                displayOrder: idx,
            });
        } else {
            let userInputs = {
                billingMode: foundBillingMode || cc.chargingType,
                frequency: foundFrequency || cc.serviceFrequency,
            };
            userInputs = this.extendsDevicePricingItemUserInputs(userInputs, cc)
            return new DevicePricingItem().copyWith({
                id: generatedId,
                device: article,
                userInputs,
                prefilledInputs,
                pricingItemType: EnumPricingItemType.DEVICE,
                isCmc: Boolean(isServiceCmc && cc.keyAccountCode),
                matchingId: generatedId,
                displayOrder: idx,
            });
        }
    }

    protected getPrefilledInputs(cc: BillingLine, referential: any, productPricings: ProductPricingV2[]): any {
        let foundFrequency = referential.frequencies.find(item => item.frequency == cc.serviceFrequency);
        if (foundFrequency) {
            foundFrequency = ({
                key: foundFrequency.frequency,
                value: foundFrequency.frequency
            });
        }
        let frequencies = [foundFrequency];
        if (productPricings?.length > 0) {
            let frequencyList = Array.from(
                new Set(
                    productPricings.flatMap(
                        pp => pp.productPricingDetails['frequency']
                    )
                )
            );
            const referentialFrequencies = referential.frequencies.filter(f => frequencyList.includes(f.frequency) && f.frequency !== foundFrequency.key);
            referentialFrequencies.forEach(f => {
                frequencies.push({
                    key: f.frequency,
                    value: f.frequency
                });
            });
        }
        return {
            frequencies: frequencies,
        };
    };

    protected extendsDevicePricingItemUserInputs(userInputs: any, cc: BillingLine): any {
        Object.assign(userInputs, {
            quantity: cc.monthlyQuantity
        })
        return userInputs;
    }



}
